import React, { useState, useEffect } from 'react';
import { adminManagementService } from '../../services/adminService';
import { Shield, ShieldAlert, Trash2, UserPlus, Mail, User } from 'lucide-react';
import './AdminPages.css';

function AdminAdmins() {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        fullName: '',
        isSuperAdmin: false
    });

    useEffect(() => {
        loadAdmins();
    }, []);

    const loadAdmins = async () => {
        try {
            setLoading(true);
            const data = await adminManagementService.getAdmins();
            setAdmins(data);
        } catch (err) {
            console.error('Failed to load admins', err);
            setError('Failed to load administrators. You may not have permission.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            await adminManagementService.createAdmin(formData);
            setSuccess('Administrator created successfully');
            setFormData({ email: '', password: '', fullName: '', isSuperAdmin: false });
            loadAdmins();
            setTimeout(() => setShowModal(false), 1500);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create administrator');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to remove this administrator?')) return;
        try {
            await adminManagementService.deleteAdmin(id);
            loadAdmins();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to delete administrator');
        }
    };

    if (loading) return <div className="admin-loading">Loading administrators...</div>;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <h2>Administrator Management ({admins.length})</h2>
                <button className="btn admin-btn-primary" onClick={() => setShowModal(true)}>
                    <UserPlus size={18} />
                    <span>Add Admin</span>
                </button>
            </div>

            {error && !showModal && <div className="admin-form-error" style={{ marginBottom: '20px' }}>{error}</div>}

            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {admins.map(admin => (
                            <tr key={admin.id}>
                                <td>
                                    <div className="user-cell">
                                        <div className="user-avatar-sm" style={{ backgroundColor: admin.isSuperAdmin ? '#f59e0b' : '#38bdf8' }}>
                                            {admin.fullName?.[0] || admin.email?.[0] || 'A'}
                                        </div>
                                        <span>{admin.fullName || 'No Name'}</span>
                                    </div>
                                </td>
                                <td>{admin.email}</td>
                                <td>
                                    {admin.isSuperAdmin ? (
                                        <span className="status-badge warning" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                                            <ShieldAlert size={14} /> Superadmin
                                        </span>
                                    ) : (
                                        <span className="status-badge success">
                                            <Shield size={14} /> Admin
                                        </span>
                                    )}
                                </td>
                                <td>{new Date(admin.createdAt).toLocaleDateString()}</td>
                                <td>
                                    <button
                                        className="icon-btn delete"
                                        onClick={() => handleDelete(admin.id)}
                                        title="Remove Admin"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Create Admin Modal */}
            {showModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal">
                        <div className="admin-modal-header">
                            <h3>Add New Administrator</h3>
                            <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleSubmit} className="admin-modal-body">
                            {error && <div className="admin-form-error">{error}</div>}
                            {success && <div className="admin-form-success">{success}</div>}

                            <div className="form-group">
                                <label>Email Address</label>
                                <div className="input-with-icon">
                                    <Mail size={16} />
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        placeholder="admin@rova.ai"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Full Name</label>
                                <div className="input-with-icon">
                                    <User size={16} />
                                    <input
                                        type="text"
                                        name="fullName"
                                        placeholder="John Doe"
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Password</label>
                                <div className="input-with-icon">
                                    <Shield size={16} />
                                    <input
                                        type="password"
                                        name="password"
                                        required
                                        minLength="6"
                                        placeholder="••••••••"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group checkbox-group" style={{ marginTop: '20px' }}>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="isSuperAdmin"
                                        checked={formData.isSuperAdmin}
                                        onChange={handleInputChange}
                                    />
                                    <span>Grant Superadmin privileges</span>
                                </label>
                                <p className="help-text">Superadmins can manage other administrators.</p>
                            </div>

                            <div className="admin-modal-footer">
                                <button type="button" className="admin-cancel-btn" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="admin-save-btn" disabled={isSaving}>
                                    {isSaving ? 'Creating...' : 'Create Administrator'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminAdmins;
